import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import Dashboard from './pages/Dashboard';
import PracticePage from './pages/PracticePage';
import LeaderboardPage from './pages/LeaderboardPage';
import ProgressPage from './pages/ProgressPage';
import ProfilePage from './pages/ProfilePage';
import BrainArena from './pages/BrainArena';
import ChapterList from './pages/ChapterList';
import ChapterTest from './pages/ChapterTest';
import ChapterLearn from './pages/ChapterLearn';
import StudyHub from './pages/StudyHub';
import TeacherDashboard from './pages/TeacherDashboard';
import ParentDashboard from './pages/ParentDashboard';
import SchoolSignup from './pages/SchoolSignup';
import BooksLibrary from './pages/BooksLibrary';
import HomeworkScanner from './pages/HomeworkScanner';
import Competitions from './pages/Competitions';
import AdminDashboard from './pages/AdminDashboard';
import VideoLab from './pages/VideoLab';
import Paywall from './pages/Paywall';
import SubscriptionGate from './components/SubscriptionGate';

// Games
import MathBlitz from './games/MathBlitz';
import FractionPizza from './games/FractionPizza';
import NumberNinja from './games/NumberNinja';
import ShapeBuilder from './games/ShapeBuilder';
import TimesTableRace from './games/TimesTableRace';
import AtomBuilder from './games/AtomBuilder';
import ElementMatch from './games/ElementMatch';
import BodyExplorer from './games/BodyExplorer';
import CircuitConnect from './games/CircuitConnect';
import PeriodicPuzzle from './games/PeriodicPuzzle';
import WordScramble from './games/WordScramble';
import SentenceFixer from './games/SentenceFixer';
import SynonymSprint from './games/SynonymSprint';
import SpellBee from './games/SpellBee';
import StoryBuilder from './games/StoryBuilder';
import StoryGamesHub from './games/StoryGamesHub';
import StoryGameEngine from './games/StoryGameEngine';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center"><div className="w-16 h-16 border-4 border-[#4D96FF] border-t-transparent rounded-full animate-spin"></div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <SubscriptionGate>{children}</SubscriptionGate>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center"><div className="w-16 h-16 border-4 border-[#4D96FF] border-t-transparent rounded-full animate-spin"></div></div>;
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/school-signup" element={<PublicRoute><SchoolSignup /></PublicRoute>} />
      <Route path="/paywall" element={<Paywall />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/teacher" element={<ProtectedRoute><TeacherDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/parent" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
      <Route path="/books" element={<ProtectedRoute><BooksLibrary /></ProtectedRoute>} />
      <Route path="/homework" element={<ProtectedRoute><HomeworkScanner /></ProtectedRoute>} />
      <Route path="/competitions" element={<ProtectedRoute><Competitions /></ProtectedRoute>} />
      <Route path="/videos" element={<ProtectedRoute><VideoLab /></ProtectedRoute>} />
      <Route path="/practice/:subject" element={<ProtectedRoute><PracticePage /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/games" element={<ProtectedRoute><BrainArena /></ProtectedRoute>} />
      <Route path="/chapters/:subject" element={<ProtectedRoute><ChapterList /></ProtectedRoute>} />
      <Route path="/chapters/:subject/:chapterId/test" element={<ProtectedRoute><ChapterTest /></ProtectedRoute>} />
      <Route path="/chapters/:subject/:chapterId/learn" element={<ProtectedRoute><ChapterLearn /></ProtectedRoute>} />
      <Route path="/study-hub" element={<ProtectedRoute><StudyHub /></ProtectedRoute>} />
      {/* Math Games */}
      <Route path="/games/math-blitz" element={<ProtectedRoute><MathBlitz /></ProtectedRoute>} />
      <Route path="/games/fraction-pizza" element={<ProtectedRoute><FractionPizza /></ProtectedRoute>} />
      <Route path="/games/number-ninja" element={<ProtectedRoute><NumberNinja /></ProtectedRoute>} />
      <Route path="/games/shape-builder" element={<ProtectedRoute><ShapeBuilder /></ProtectedRoute>} />
      <Route path="/games/times-table-race" element={<ProtectedRoute><TimesTableRace /></ProtectedRoute>} />
      {/* Science Games */}
      <Route path="/games/atom-builder" element={<ProtectedRoute><AtomBuilder /></ProtectedRoute>} />
      <Route path="/games/element-match" element={<ProtectedRoute><ElementMatch /></ProtectedRoute>} />
      <Route path="/games/body-explorer" element={<ProtectedRoute><BodyExplorer /></ProtectedRoute>} />
      <Route path="/games/circuit-connect" element={<ProtectedRoute><CircuitConnect /></ProtectedRoute>} />
      <Route path="/games/periodic-puzzle" element={<ProtectedRoute><PeriodicPuzzle /></ProtectedRoute>} />
      {/* English Games */}
      <Route path="/games/word-scramble" element={<ProtectedRoute><WordScramble /></ProtectedRoute>} />
      <Route path="/games/sentence-fixer" element={<ProtectedRoute><SentenceFixer /></ProtectedRoute>} />
      <Route path="/games/synonym-sprint" element={<ProtectedRoute><SynonymSprint /></ProtectedRoute>} />
      <Route path="/games/spell-bee" element={<ProtectedRoute><SpellBee /></ProtectedRoute>} />
      <Route path="/games/story-builder" element={<ProtectedRoute><StoryBuilder /></ProtectedRoute>} />
      <Route path="/games/story" element={<ProtectedRoute><StoryGamesHub /></ProtectedRoute>} />
      <Route path="/games/story/:gameId" element={<ProtectedRoute><StoryGameEngine /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Toaster position="top-right" expand={false} richColors closeButton />
          <AppRoutes />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
